/**
 * Derives the real setup values for a tenant: the hostnames it answers on, the
 * Entra redirect URIs, and the DNS records its custom domain needs.
 *
 * The Setup Guide used to print fixed examples - `your-app.azurewebsites.net`,
 * `learning.acme.com`, `asuid.learning` - each with a copy button beside it. An
 * administrator copying the redirect URI pasted a string that could never work
 * into a client's Entra app registration, and the mistake only surfaced later as
 * a failed sign-in. Everything here is computed from the tenant being viewed and
 * the host the panel is served from, so what is on screen is what to use.
 *
 * Anything that genuinely cannot be derived - the App Service hostname when the
 * panel is served through a front door, and the Azure domain verification ID -
 * is reported as missing rather than filled with a placeholder. The caller shows
 * a prompt for it instead of offering a fake value to copy.
 */

const SSO_CALLBACK_PATH = '/api/sso/callback/azure';

// Second-level labels that sit under a country code, so the registrable domain
// is three labels rather than two. Without this `oldmutual.co.za` parses as the
// subdomain "oldmutual" of "co.za" and the DNS records come out wrong.
const SECOND_LEVEL_LABELS = new Set([
  'co', 'com', 'net', 'org', 'gov', 'edu', 'ac', 'mil', 'sch', 'web'
]);

export const APP_SERVICE_HOST_KEY = 'cerebro.setup.appServiceHost';
export const VERIFICATION_ID_KEY = 'cerebro.setup.verificationId';

/** Strip scheme, path, port, trailing dot and case from anything host-shaped. */
export function normalizeHost(value) {
  if (!value || typeof value !== 'string') return '';
  return value
    .trim()
    .replace(/^[a-z][a-z0-9+.-]*:\/\//i, '')
    .split('/')[0]
    .split(':')[0]
    .replace(/\.+$/, '')
    .toLowerCase();
}

/**
 * Split a hostname into its registrable domain and the labels below it.
 * Returns null for anything that is not a usable hostname.
 */
export function splitHost(value) {
  const host = normalizeHost(value);
  if (!host || !host.includes('.')) return null;

  const parts = host.split('.');
  if (parts.some((p) => p === '')) return null;

  const registrableLength =
    parts.length >= 3 && SECOND_LEVEL_LABELS.has(parts[parts.length - 2]) ? 3 : 2;
  if (parts.length < registrableLength) return null;

  const labels = parts.slice(0, parts.length - registrableLength);
  return {
    host,
    registrable: parts.slice(-registrableLength).join('.'),
    subdomain: labels.join('.'),
    isApex: labels.length === 0
  };
}

/**
 * The hostnames that resolve to one tenant.
 *
 * Mirrors `_host_candidates` in backend/routes/tenant_public.py - the API
 * matches the apex and the www form interchangeably, so both reach the tenant
 * once bound, and both therefore need registering as redirect URIs.
 */
export function hostCandidates(value) {
  const host = normalizeHost(value);
  if (!host) return [];
  return host.startsWith('www.') ? [host, host.slice(4)] : [host, `www.${host}`];
}

/**
 * Hostnames a tenant's users can sign in on, most canonical first.
 * Falls back to the slug subdomain only when no custom domain is stored.
 */
export function tenantSignInHosts(tenant, fallbackDomain) {
  const custom = normalizeHost(tenant?.custom_domain);
  if (custom) return hostCandidates(custom);

  const slug = tenant?.slug;
  const base = normalizeHost(fallbackDomain);
  return slug && base ? [`${slug}.${base}`] : [];
}

/** Entra redirect URIs, built the way the backend builds its callback URL. */
export function redirectUris(hosts) {
  return (hosts || []).filter(Boolean).map((h) => `https://${h}${SSO_CALLBACK_PATH}`);
}

/** The tenant's SSO entry point, matching the /login/{tenant_slug} route. */
export function ssoLoginPath(tenant) {
  return tenant?.slug ? `/api/sso/login/${tenant.slug}` : null;
}

/**
 * DNS records a custom domain needs before Azure will validate it.
 *
 * `value: null` means the value is not knowable from the browser; the caller
 * renders a prompt rather than a copyable string.
 */
export function dnsRecords(customDomain, { appServiceHost } = {}, verificationId) {
  const parts = splitHost(customDomain);
  if (!parts) return [];

  const target = normalizeHost(appServiceHost) || null;
  const id = (verificationId || '').trim() || null;

  if (parts.isApex) {
    return [
      {
        type: 'A',
        name: '@',
        value: null,
        // A CNAME is not legal at a zone apex. Azure publishes an inbound IP per
        // App Service; providers offering ALIAS/ANAME can point at the hostname
        // instead and avoid pinning the address.
        hint: target
          ? `Inbound IP from App Service -> Custom domains, or an ALIAS/ANAME to ${target}`
          : 'Inbound IP from App Service -> Custom domains'
      },
      { type: 'TXT', name: 'asuid', value: id, hint: id ? null : 'Custom Domain Verification ID' }
    ];
  }

  return [
    {
      type: 'CNAME',
      name: parts.subdomain,
      value: target,
      hint: target ? null : 'App Service hostname'
    },
    {
      type: 'TXT',
      name: `asuid.${parts.subdomain}`,
      value: id,
      hint: id ? null : 'Custom Domain Verification ID'
    }
  ];
}

/**
 * The super-admin call that turns SSO on for this tenant.
 *
 * `sso_provider` is "azure_ad" verbatim: the sign-in path rejects every other
 * value, including the "entra_id" that appears as a default elsewhere in the API.
 */
export function ssoConfigExample(tenant) {
  return {
    method: 'PUT',
    path: `/api/master/organizations/${tenant?.org_id || '<org_id>'}`,
    body: {
      sso_enabled: true,
      sso_provider: 'azure_ad',
      sso_config: {
        tenant_id: '<Entra directory (tenant) ID>',
        client_id: '<Application (client) ID>',
        client_secret: `keyvault:${tenant?.slug || '<tenant>'}-entra-secret`
      }
    }
  };
}

function readStored(key) {
  // Throws outright in some privacy modes, so never let it break the panel.
  try {
    return window.localStorage.getItem(key) || '';
  } catch (err) {
    return '';
  }
}

export function storeSetupValue(key, value) {
  try {
    if (value) window.localStorage.setItem(key, value);
    else window.localStorage.removeItem(key);
  } catch (err) {
    /* nothing to do - the panel still works, it just will not remember */
  }
}

/**
 * The App Service hostname to use as a CNAME target.
 *
 * Prefers explicit configuration, then the host actually serving the panel when
 * that is the App Service itself. Behind a front door - Cloudflare, Front Door -
 * the browser cannot see the origin, so it falls back to a value the
 * administrator supplied once. Returns '' rather than guessing.
 */
export function detectAppServiceHost(win) {
  const w = win || (typeof window !== 'undefined' ? window : undefined);
  const configured = normalizeHost(process.env.REACT_APP_APP_SERVICE_HOST);
  if (configured) return configured;

  const current = normalizeHost(w?.location?.host);
  if (current.endsWith('.azurewebsites.net')) return current;

  return readStored(APP_SERVICE_HOST_KEY);
}

/**
 * The Azure Custom Domain Verification ID.
 *
 * A property of the App Service, so it cannot be derived in the browser. It is
 * not a secret - it is published in DNS as a TXT record - so remembering it
 * locally costs nothing and saves a trip to the portal for every tenant.
 */
export function detectVerificationId() {
  const configured = (process.env.REACT_APP_AZURE_DOMAIN_VERIFICATION_ID || '').trim();
  return configured || readStored(VERIFICATION_ID_KEY);
}
